import type { ApplicationStatus } from '~domains/transactions/entities/application';

import type { Merchant } from '~entities/merchant';
import type { Pos } from '~entities/pos';

import {
  getCorrectDateRange,
  keepExistingIds,
  readPersistedJson,
  writePersistedJson,
} from '~shared/lib/persisted-filters';
import type { DateRange } from '~shared/ui/date-range-picker';

import { endOfDay, startOfDay, subDays } from 'date-fns';

export type ApplicationsFiltersValue = {
  period: DateRange;
  merchantIds: string[];
  posIds: string[];
  statuses: ApplicationStatus[];
};

export const APPLICATIONS_FILTERS_STORAGE_KEY = 'applicationsFilters.v2';

export const DEFAULT_APPLICATIONS_PERIOD_DAYS = 7;

export const getDefaultApplicationsFilter = (): ApplicationsFiltersValue => ({
  period: {
    startDate: startOfDay(
      subDays(new Date(), DEFAULT_APPLICATIONS_PERIOD_DAYS)
    ).toISOString(),
    endDate: endOfDay(new Date()).toISOString(),
  },
  merchantIds: [],
  posIds: [],
  statuses: [],
});

export const restoreApplicationsFilters = (
  merchants: Merchant[],
  posList: Pos[]
): ApplicationsFiltersValue => {
  const defaults = getDefaultApplicationsFilter();
  const saved = readPersistedJson<Partial<ApplicationsFiltersValue>>(
    APPLICATIONS_FILTERS_STORAGE_KEY
  );

  if (!saved) {
    return defaults;
  }

  return {
    period: getCorrectDateRange(saved.period, defaults.period),
    merchantIds: keepExistingIds(
      saved.merchantIds,
      merchants.map(({ id }) => id)
    ),
    posIds: keepExistingIds(
      saved.posIds,
      posList.map(({ id }) => id)
    ),
    statuses: saved.statuses ?? [],
  };
};

export const persistApplicationsFilters = (
  filters: ApplicationsFiltersValue
) => {
  writePersistedJson(APPLICATIONS_FILTERS_STORAGE_KEY, filters);
};
